import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import Select from 'react-select';
import { EditorState, convertToRaw, convertFromRaw, Modifier } from 'draft-js';
import ResizableImage from './ResizableImage';
import { draftContentStateToHtml } from '../../util/draftToHtml';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import Swal from 'sweetalert2';
import { customDropdownWithBorder } from '../../util/reactCustomStyles';
import { createQuestion, updateQuestion, fetchQuestions } from '../../redux/QuestionBank/QuestionsAction';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../redux/QuestionBank/SubjectsUnitsAction';
import { fetchPassages } from '../../redux/QuestionBank/PassageAction';
import { questionTextToolbar, choiceToolbar, explanationToolbar, lessonReferenceToolbar, editorContainerStyles } from '../../util/editorToolbarConfig';
import { useUnsavedChangesWarning } from '../../util/useUnsavedChangesWarning';

const QuestionBuilder = ({ userEmail, editingQuestion = null, preselectedPassage = null, onQuestionSaved = null, onEditSave = null }) => {
  const dispatch = useDispatch();

  // Redux state
  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);
  const { topics } = useSelector(state => state.questionBankTopics);
  const { passages } = useSelector(state => state.questionBankPassages);
  const { loading } = useSelector(state => state.questionBankQuestions);

  // Editor states
  const [questionTextEditor, setQuestionTextEditor] = useState(EditorState.createEmpty());
  const [explanationTextEditor, setExplanationTextEditor] = useState(EditorState.createEmpty());
  const [lessonReferenceEditor, setLessonReferenceEditor] = useState(EditorState.createEmpty());

  // Form state
  const [formData, _setFormData] = useState({
    subjectId: '',
    unitId: '',
    topicId: '',
    difficulty: 'medium',
    quality: 'simple',
    tags: [],
    isPassageBased: false,
    passageId: ''
  });
  const setFormData = (val) => { _setFormData(val); setIsDirty(true); };

  // Choices state
  const [choices, setChoices] = useState([
    { choiceId: '', text: EditorState.createEmpty() },
    { choiceId: '', text: EditorState.createEmpty() }
  ]);

  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Warn on tab close / browser navigation when form has unsaved changes
  useUnsavedChangesWarning(isDirty);

  // Refs to always have the latest editor states (avoids stale closures)
  const questionTextEditorRef = useRef(questionTextEditor);
  questionTextEditorRef.current = questionTextEditor;
  const explanationTextEditorRef = useRef(explanationTextEditor);
  explanationTextEditorRef.current = explanationTextEditor;
  const lessonReferenceEditorRef = useRef(lessonReferenceEditor);
  lessonReferenceEditorRef.current = lessonReferenceEditor;
  const choicesRef = useRef(choices);
  choicesRef.current = choices;

  // Stable resize handler factory using refs
  const createResizeHandler = useCallback((getEditorState, setEditorState) => (entityKey, newWidth) => {
    const editorState = getEditorState();
    const contentState = editorState.getCurrentContent();
    const newContentState = contentState.mergeEntityData(entityKey, { width: String(newWidth) });
    const newEditorState = EditorState.push(editorState, newContentState, 'apply-entity');
    setEditorState(newEditorState);
    setIsDirty(true);
  }, []);

  // Stable resize handlers (never change identity)
  const questionTextResize = useCallback((entityKey, newWidth) => {
    createResizeHandler(() => questionTextEditorRef.current, setQuestionTextEditor)(entityKey, newWidth);
  }, [createResizeHandler]);

  const explanationResize = useCallback((entityKey, newWidth) => {
    createResizeHandler(() => explanationTextEditorRef.current, setExplanationTextEditor)(entityKey, newWidth);
  }, [createResizeHandler]);

  const lessonReferenceResize = useCallback((entityKey, newWidth) => {
    createResizeHandler(() => lessonReferenceEditorRef.current, setLessonReferenceEditor)(entityKey, newWidth);
  }, [createResizeHandler]);

  const choiceResize = useCallback((index) => (entityKey, newWidth) => {
    const editorState = choicesRef.current[index].text;
    const contentState = editorState.getCurrentContent();
    const newContentState = contentState.mergeEntityData(entityKey, { width: String(newWidth) });
    const newEditorState = EditorState.push(editorState, newContentState, 'apply-entity');
    handleChoiceTextChange(index, newEditorState);
    setIsDirty(true);
  }, []);

  // Stable block renderer - always returns same props object identity for each editor
  const imageBlockRenderer = useCallback((onResizeFn) => (block) => {
    if (block.getType() === 'atomic') {
      return {
        component: ResizableImage,
        editable: false,
        props: { onResize: onResizeFn },
      };
    }
    return undefined;
  }, []);

  const questionTextBlockRenderer = useCallback(imageBlockRenderer(questionTextResize), [imageBlockRenderer, questionTextResize]);
  const explanationBlockRenderer = useCallback(imageBlockRenderer(explanationResize), [imageBlockRenderer, explanationResize]);
  const lessonReferenceBlockRenderer = useCallback(imageBlockRenderer(lessonReferenceResize), [imageBlockRenderer, lessonReferenceResize]);

  // Filtered units based on selected subject
  const filteredUnits = formData.subjectId
    ? units.filter(unit => unit.subjectId === formData.subjectId)
    : [];

  // Filtered topics based on selected unit
  const filteredTopics = formData.unitId
    ? topics.filter(topic => topic.unitId === formData.unitId)
    : [];

  // Filtered passages based on selected subject and unit
  const filteredPassages = formData.subjectId && formData.unitId
    ? passages.filter(passage =>
        passage.subjectId === formData.subjectId &&
        passage.unitId === formData.unitId
      )
    : [];

  useEffect(() => {
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
    dispatch(fetchPassages());
  }, [dispatch]);

  // Load editing question data
  useEffect(() => {
    if (editingQuestion) {
      // M2: Wrap convertFromRaw calls in try/catch to prevent crashes from malformed JSON
      try {
      // Load question text
      if (editingQuestion.questionText) {
        setQuestionTextEditor(EditorState.createWithContent(convertFromRaw(editingQuestion.questionText)));
      }

      // Load explanation
      if (editingQuestion.explanationText) {
        setExplanationTextEditor(EditorState.createWithContent(convertFromRaw(editingQuestion.explanationText)));
      }

      // Load lesson reference
      if (editingQuestion.lessonReference) {
        setLessonReferenceEditor(EditorState.createWithContent(convertFromRaw(editingQuestion.lessonReference)));
      }

      // Load choices
      if (editingQuestion.choices && editingQuestion.choices.length > 0) {
        const loadedChoices = editingQuestion.choices.map(choice => ({
          choiceId: choice.choiceId,
          text: choice.text ? EditorState.createWithContent(convertFromRaw(choice.text)) : EditorState.createEmpty()
        }));
        setChoices(loadedChoices);

        // H5 fix: map old correctAnswer choiceIds to current indices
        // On submit, choices get sequential index-based IDs (0, 1, 2...),
        // so correctAnswers must reference those indices, not old choiceIds
        if (editingQuestion.correctAnswer && editingQuestion.correctAnswer.length > 0) {
          const mappedCorrectAnswers = editingQuestion.correctAnswer
            .map(oldId => {
              const index = editingQuestion.choices.findIndex(c => c.choiceId === oldId);
              return index >= 0 ? index.toString() : null;
            })
            .filter(Boolean);
          setCorrectAnswers(mappedCorrectAnswers);
        } else {
          setCorrectAnswers([]);
        }
      } else {
        setCorrectAnswers(editingQuestion.correctAnswer || []);
      }
      } catch (err) {
        console.error('M2: Failed to parse question Draft.js content:', err);
        Swal.fire({
          icon: 'warning',
          title: 'Content Load Error',
          text: 'Some question content could not be loaded. The stored data may be corrupted. You can re-enter the content manually.',
          confirmButtonColor: '#5e72e4'
        });
      }

      // Load form data (use _setFormData to avoid marking as dirty on initial load)
      _setFormData({
        subjectId: editingQuestion.subjectId || '',
        unitId: editingQuestion.unitId || '',
        topicId: editingQuestion.topicId || '',
        difficulty: editingQuestion.difficulty || 'medium',
        quality: editingQuestion.quality || 'simple',
        tags: editingQuestion.tags || [],
        isPassageBased: !!editingQuestion.passageId,
        passageId: editingQuestion.passageId || ''
      });
      setIsDirty(false);
    }
  }, [editingQuestion]);

  // Handle preselected passage
  useEffect(() => {
    if (preselectedPassage && !editingQuestion) {
      _setFormData(prev => ({
        ...prev,
        subjectId: preselectedPassage.subjectId,
        unitId: preselectedPassage.unitId,
        topicId: preselectedPassage.topicId || '',
        isPassageBased: true,
        passageId: preselectedPassage.passageId
      }));

      // Show success message
      Swal.fire({
        title: 'Passage Selected',
        text: `Creating question for passage: ${preselectedPassage.title}`,
        icon: 'info',
        timer: 2000,
        showConfirmButton: false
      });
    }
  }, [preselectedPassage, editingQuestion]);

  const handleAddChoice = () => {
    setChoices([...choices, { choiceId: '', text: EditorState.createEmpty() }]);
  };

  const handleRemoveChoice = (index) => {
    if (choices.length <= 2) {
      Swal.fire('Error', 'You must have at least 2 choices', 'error');
      return;
    }
    const newChoices = choices.filter((_, i) => i !== index);
    setChoices(newChoices);

    // H5 fix: re-map correctAnswers to new indices after removal
    // Since correctAnswers uses index-based IDs, removing a choice shifts indices
    const removedIndexStr = index.toString();
    const updatedCorrectAnswers = correctAnswers
      .filter(id => id !== removedIndexStr) // remove the deleted choice
      .map(id => {
        const oldIndex = parseInt(id);
        // Shift down indices above the removed one
        return oldIndex > index ? (oldIndex - 1).toString() : id;
      });
    setCorrectAnswers(updatedCorrectAnswers);
  };

  const handleChoiceTextChange = (index, editorState) => {
    const newChoices = [...choices];
    newChoices[index].text = editorState;
    setChoices(newChoices);
  };

  const handleCorrectAnswerToggle = (index) => {
    const choiceId = index.toString();
    setCorrectAnswers([choiceId]);
  };

  const handleAddTag = () => {
    if (tagInput.trim()) {
      // Split by comma and filter out empty strings and duplicates
      const newTags = tagInput
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag && !formData.tags.includes(tag));

      if (newTags.length > 0) {
        setFormData({
          ...formData,
          tags: [...formData.tags, ...newTags]
        });
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(t => t !== tag)
    });
  };

  const validateForm = () => {
    if (!formData.subjectId) {
      Swal.fire('Error', 'Please select a subject', 'error');
      return false;
    }

    if (!formData.unitId) {
      Swal.fire('Error', 'Please select a unit/discipline', 'error');
      return false;
    }

    if (formData.isPassageBased && !formData.passageId) {
      Swal.fire('Error', 'Please select a passage for this passage-based question', 'error');
      return false;
    }

    const questionContent = questionTextEditor.getCurrentContent();
    if (!questionContent.hasText()) {
      Swal.fire('Error', 'Please enter the question text', 'error');
      return false;
    }

    // Check if all choices have text
    for (let i = 0; i < choices.length; i++) {
      if (!choices[i].text.getCurrentContent().hasText()) {
        Swal.fire('Error', `Please enter text for choice ${i + 1}`, 'error');
        return false;
      }
    }

    if (correctAnswers.length !== 1) {
      Swal.fire('Error', 'Please select the correct answer', 'error');
      return false;
    }

    const explanationContent = explanationTextEditor.getCurrentContent();
    if (!explanationContent.hasText()) {
      Swal.fire('Error', 'Please enter an explanation', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // M1: Show loading dialog properly via Swal.fire with didOpen
    Swal.fire({
      title: editingQuestion ? 'Updating Question...' : 'Creating Question...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    // Prepare choices with IDs
    const preparedChoices = choices.map((choice, index) => ({
      choiceId: index.toString(),
      text: convertToRaw(choice.text.getCurrentContent())
    }));

    const questionData = {
      questionText: convertToRaw(questionTextEditor.getCurrentContent()),
      choices: preparedChoices,
      correctAnswer: correctAnswers,
      explanationText: convertToRaw(explanationTextEditor.getCurrentContent()),
      lessonReference: convertToRaw(lessonReferenceEditor.getCurrentContent()),
      subjectId: formData.subjectId,
      unitId: formData.unitId,
      ...(formData.topicId ? { topicId: formData.topicId } : {}),
      difficulty: formData.difficulty,
      quality: formData.quality,
      tags: formData.tags,
      source: 'manual',
      status: 'active',
      ...(formData.isPassageBased && formData.passageId ? { passageId: formData.passageId } : {})
    };

    // If onEditSave is provided, return the data to the caller instead of saving to database
    if (onEditSave) {
      Swal.close();
      onEditSave(questionData);
      return;
    }

    let result;
    if (editingQuestion) {
      result = await dispatch(updateQuestion(editingQuestion.questionId, questionData));
    } else {
      const subjectName = subjects?.find(s => s.subjectId === formData.subjectId)?.name;
      result = await dispatch(createQuestion(questionData, userEmail, subjectName));
    }

    Swal.close();

    if (result.success) {
      Swal.fire('Success', `Question ${editingQuestion ? 'updated' : 'created'} successfully!`, 'success');

      // Reset form
      resetForm();

      // Refresh questions list
      dispatch(fetchQuestions());

      // Call callback if provided
      if (onQuestionSaved) {
        onQuestionSaved();
      }
    } else {
      Swal.fire('Error', result.error, 'error');
    }
  };

  const resetForm = () => {
    setQuestionTextEditor(EditorState.createEmpty());
    setExplanationTextEditor(EditorState.createEmpty());
    setLessonReferenceEditor(EditorState.createEmpty());
    setChoices([
      { choiceId: '', text: EditorState.createEmpty() },
      { choiceId: '', text: EditorState.createEmpty() }
    ]);
    setCorrectAnswers([]);
    _setFormData({
      subjectId: '',
      unitId: '',
      topicId: '',
      difficulty: 'medium',
      quality: 'simple',
      tags: [],
      isPassageBased: false,
      passageId: ''
    });
    setTagInput('');
    setIsDirty(false);
  };

  const getChoiceLabel = (index) => {
    return String.fromCharCode(65 + index); // 65 is ASCII for 'A'
  };

  return (
    <Card className="shadow">
      <CardHeader className="bg-transparent">
        <h3 className="mb-0">
          {editingQuestion ? 'Edit Question' : 'Create New Question'}
        </h3>
      </CardHeader>
      <CardBody>
        <Form onSubmit={handleSubmit}>
          {/* Subject and Unit Selection */}
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">
                  Subject <span className="text-danger">*</span>
                </Label>
                <Select
                  options={subjects.map(subject => ({
                    value: subject.subjectId,
                    label: subject.name
                  }))}
                  value={
                    formData.subjectId
                      ? {
                          value: formData.subjectId,
                          label: (() => {
                            const s = subjects.find(s => s.subjectId === formData.subjectId);
                            return s?.name;
                          })()
                        }
                      : null
                  }
                  onChange={(option) => setFormData({ ...formData, subjectId: option?.value || '', unitId: '', topicId: '' })}
                  styles={customDropdownWithBorder}
                  placeholder="Select a subject"
                  isClearable
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">
                  Unit <span className="text-danger">*</span>
                </Label>
                <Select
                  options={filteredUnits.map(unit => ({
                    value: unit.unitId,
                    label: unit.name
                  }))}
                  value={
                    formData.unitId
                      ? {
                          value: formData.unitId,
                          label: (() => {
                            const u = filteredUnits.find(u => u.unitId === formData.unitId);
                            return u?.name;
                          })()
                        }
                      : null
                  }
                  onChange={(option) => setFormData({ ...formData, unitId: option?.value || '', topicId: '' })}
                  styles={customDropdownWithBorder}
                  placeholder="Select a unit"
                  isDisabled={!formData.subjectId}
                  isClearable
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">Topic (Optional)</Label>
                <Select
                  options={filteredTopics.map(topic => ({
                    value: topic.topicId,
                    label: topic.name
                  }))}
                  value={
                    formData.topicId
                      ? {
                          value: formData.topicId,
                          label: (() => {
                            const t = filteredTopics.find(t => t.topicId === formData.topicId);
                            return t?.name;
                          })()
                        }
                      : null
                  }
                  onChange={(option) => setFormData({ ...formData, topicId: option?.value || '' })}
                  styles={customDropdownWithBorder}
                  placeholder="Select a topic"
                  isDisabled={!formData.unitId || filteredTopics.length === 0}
                  isClearable
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Passage-Based Question Toggle */}
          <FormGroup>
            <div className="custom-control custom-checkbox">
              <input
                className="custom-control-input"
                id="isPassageBasedCheckbox"
                type="checkbox"
                checked={formData.isPassageBased}
                onChange={(e) => setFormData({
                  ...formData,
                  isPassageBased: e.target.checked,
                  passageId: e.target.checked ? formData.passageId : ''
                })}
              />
              <label
                className="custom-control-label"
                htmlFor="isPassageBasedCheckbox"
                style={{ color: '#32325d', fontWeight: '600' }}
              >
                This is a passage-based question
              </label>
            </div>
            <small className="text-muted ml-4">
              Check this if the question requires a reading passage
            </small>
          </FormGroup>

          {/* Passage Selection - Only shown when isPassageBased is true */}
          {formData.isPassageBased && (
            <FormGroup>
              <Label className="form-control-label" style={{ color: '#32325d', fontWeight: '600' }}>
                Select Passage <span className="text-danger">*</span>
              </Label>
              <Select
                options={filteredPassages.map(passage => ({
                  value: passage.passageId,
                  label: passage.title
                }))}
                value={
                  formData.passageId
                    ? {
                        value: formData.passageId,
                        label: filteredPassages.find(p => p.passageId === formData.passageId)?.title
                      }
                    : null
                }
                onChange={(option) => setFormData({ ...formData, passageId: option?.value || '' })}
                styles={customDropdownWithBorder}
                placeholder={
                  filteredPassages.length === 0
                    ? "No passages available for this subject/unit"
                    : "Select a passage"
                }
                isDisabled={!formData.subjectId || !formData.unitId || filteredPassages.length === 0}
                isClearable
              />
              {formData.subjectId && formData.unitId && filteredPassages.length === 0 && (
                <small className="text-warning d-block mt-1">
                  <i className="fas fa-exclamation-triangle mr-1"></i>
                  No passages found for this subject/unit. Please create a passage first in the "Manage Passages" tab.
                </small>
              )}
            </FormGroup>
          )}

          {/* Question Text */}
          <FormGroup>
            <Label className="form-control-label" style={{ color: '#32325d', fontWeight: '600' }}>
              Question Text <span className="text-danger">*</span>
            </Label>
            <div className="border rounded p-2 question-editor-container">
              <style>{editorContainerStyles}</style>
              <Editor
                editorState={questionTextEditor}
                onEditorStateChange={(state) => { setQuestionTextEditor(state); setIsDirty(true); }}
                toolbar={questionTextToolbar}
                customBlockRenderFunc={questionTextBlockRenderer}
              />
            </div>
          </FormGroup>

          {/* Choices */}
          <FormGroup>
            <Label className="form-control-label">
              Answer Choices <span className="text-danger">*</span> (Select the correct answer)
            </Label>
            {choices.map((choice, index) => (
              <div key={index} className="mb-3">
                <div className="d-flex flex-column flex-md-row align-items-start">
                  <div className="d-flex align-items-center mb-2 mb-md-0 mr-md-3" style={{ minWidth: '60px', paddingTop: '10px' }}>
                    <div className="custom-control custom-radio">
                      <input
                        type="radio"
                        className="custom-control-input"
                        id={`choice-${index}`}
                        name="correctAnswer"
                        checked={correctAnswers.includes(index.toString())}
                        onChange={() => handleCorrectAnswerToggle(index)}
                      />
                      <label className="custom-control-label" htmlFor={`choice-${index}`}></label>
                    </div>
                    <span className="ml-2 font-weight-bold text-muted">{getChoiceLabel(index)}.</span>
                  </div>
                  <div className="flex-grow-1 w-100" style={{ maxWidth: '100%' }}>
                    <div className="border rounded p-2 choice-editor-container">
                      <Editor
                        editorState={choice.text}
                        onEditorStateChange={(state) => { handleChoiceTextChange(index, state); setIsDirty(true); }}
                        toolbar={choiceToolbar}
                        customBlockRenderFunc={imageBlockRenderer(choiceResize(index))}
                      />
                    </div>
                  </div>
                  <div className="ml-2" style={{ minWidth: '40px', paddingTop: '10px' }}>
                    {choices.length > 2 && (
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleRemoveChoice(index)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <Button color="primary" size="sm" onClick={handleAddChoice}>
              <i className="fas fa-plus"></i> Add Choice
            </Button>
          </FormGroup>

          {/* Explanation */}
          <FormGroup>
            <Label className="form-control-label">
              Explanation <span className="text-danger">*</span>
            </Label>
            <div className="border rounded p-2 explanation-editor-container">
              <Editor
                editorState={explanationTextEditor}
                onEditorStateChange={(state) => { setExplanationTextEditor(state); setIsDirty(true); }}
                toolbar={explanationToolbar}
                customBlockRenderFunc={explanationBlockRenderer}
              />
            </div>
          </FormGroup>

          {/* Lesson Reference */}
          <FormGroup>
            <Label className="form-control-label">Lesson Reference (Optional)</Label>
            <div className="border rounded p-2 lesson-editor-container">
              <Editor
                editorState={lessonReferenceEditor}
                onEditorStateChange={(state) => { setLessonReferenceEditor(state); setIsDirty(true); }}
                toolbar={lessonReferenceToolbar}
                customBlockRenderFunc={lessonReferenceBlockRenderer}
              />
            </div>
          </FormGroup>

          {/* Quality hidden for now
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">
                  Quality <span className="text-danger">*</span>
                </Label>
                <Select
                  options={[
                    { value: 'simple', label: 'Simple' },
                    { value: 'excellent', label: 'Excellent' }
                  ]}
                  value={{ value: formData.quality, label: formData.quality.charAt(0).toUpperCase() + formData.quality.slice(1) }}
                  onChange={(option) => setFormData({ ...formData, quality: option.value })}
                  styles={customDropdownWithBorder}
                  placeholder="Select quality"
                />
              </FormGroup>
            </Col>
          </Row>
          */}

          {/* Tags */}
          <FormGroup>
            <Label className="form-control-label">Tags (Optional)</Label>
            <div className="d-flex mb-2">
              <Input
                type="text"
                placeholder="Add tags (separate with commas)"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <Button color="primary" className="ml-2" onClick={handleAddTag} style={{ whiteSpace: 'nowrap' }}>
                <i className="fas fa-plus mr-2"></i>
                Add
              </Button>
            </div>
            <div>
              {formData.tags.map((tag, index) => (
                <Badge key={index} color="info" className="mr-2 mb-2" style={{ fontSize: '14px' }}>
                  {tag}
                  <Button
                    close
                    size="sm"
                    className="ml-2"
                    onClick={() => handleRemoveTag(tag)}
                  />
                </Badge>
              ))}
            </div>
          </FormGroup>

          {/* Submit Buttons */}
          <Row>
            <Col xs="6" md="auto" className="d-md-none mb-2">
              {editingQuestion && (
                <Button
                  color="warning"
                  block
                  onClick={() => {
                    resetForm();
                    if (onQuestionSaved) {
                      onQuestionSaved();
                    }
                  }}
                  type="button"
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </Button>
              )}
            </Col>
            <Col xs="6" md="auto" className="d-md-none mb-2">
              <Button color="warning" block onClick={resetForm} type="button">
                <i className="fas fa-redo mr-2"></i>
                Reset
              </Button>
            </Col>
            <Col xs="6" md="auto" className="d-md-none mb-2">
              <Button color="info" block onClick={() => setPreviewOpen(true)} type="button">
                <i className="fas fa-eye mr-2"></i>
                Preview
              </Button>
            </Col>
            <Col xs="6" md="auto" className="d-md-none mb-2">
              <Button color="primary" block type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    {editingQuestion ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <i className={`fas fa-${editingQuestion ? 'save' : 'plus'} mr-2`}></i>
                    {editingQuestion ? 'Update' : 'Create'}
                  </>
                )}
              </Button>
            </Col>
          </Row>
          <div className="d-none d-md-flex justify-content-end">
            {editingQuestion && (
              <Button
                color="warning"
                className="mr-2"
                onClick={() => {
                  resetForm();
                  if (onQuestionSaved) {
                    onQuestionSaved();
                  }
                }}
                type="button"
              >
                <i className="fas fa-times mr-2"></i>
                Cancel Edit
              </Button>
            )}
            <Button color="warning" className="mr-2" onClick={resetForm} type="button">
              <i className="fas fa-redo mr-2"></i>
              {editingQuestion ? 'Clear Form' : 'Reset'}
            </Button>
            <Button color="info" className="mr-2" onClick={() => setPreviewOpen(true)} type="button">
              <i className="fas fa-eye mr-2"></i>
              Preview
            </Button>
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  {editingQuestion ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <i className={`fas fa-${editingQuestion ? 'save' : 'plus'} mr-2`}></i>
                  {editingQuestion ? 'Update Question' : 'Create Question'}
                </>
              )}
            </Button>
          </div>
        </Form>

        {/* Question Preview Modal */}
        <Modal isOpen={previewOpen} toggle={() => setPreviewOpen(false)} size="xl" scrollable>
          <ModalHeader toggle={() => setPreviewOpen(false)}>
            <i className="fas fa-eye mr-2"></i>
            Question Preview
          </ModalHeader>
          <ModalBody>
            {/* Metadata Badges */}
            <div className="mb-4 d-flex flex-wrap">
              <Badge color="primary" className="mr-2 mb-1">
                {subjects.find(s => s.subjectId === formData.subjectId)?.name || 'No subject'}
              </Badge>
              <Badge color="success" className="mr-2 mb-1">
                {filteredUnits.find(u => u.unitId === formData.unitId)?.name || 'No unit'}
              </Badge>
              {formData.topicId && (
                <Badge color="primary" pill className="mr-2 mb-1">
                  {filteredTopics.find(t => t.topicId === formData.topicId)?.name}
                </Badge>
              )}
              {/* Difficulty badge hidden for now */}
              {/* Quality badge hidden for now
              <Badge color={formData.quality === 'excellent' ? 'success' : 'info'} pill className="mr-2 mb-1">
                {formData.quality}
              </Badge>
              */}
              <span className="text-muted small">{choices.length} choices</span>
            </div>

            {/* Passage Preview (if passage-based) - Hidden for now
            {formData.isPassageBased && formData.passageId && (() => {
              const passage = passages.find(p => p.passageId === formData.passageId);
              if (passage && passage.passageText) {
                const html = draftContentStateToHtml(convertFromRaw(passage.passageText));
                return (
                  <div className="mb-4">
                    <h6 className="text-uppercase text-muted font-weight-bold mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                      <i className="fas fa-book-open mr-2"></i>Passage: {passage.title}
                    </h6>
                    <div className="p-3 bg-light border rounded" dangerouslySetInnerHTML={{ __html: html }} />
                  </div>
                );
              }
              return null;
            })()}
            */}

            {/* Question Text */}
            <div className="mb-4">
              <h6 className="text-uppercase text-muted font-weight-bold mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                <i className="fas fa-question-circle mr-2"></i>Full Question
              </h6>
              {(() => {
                try {
                  const contentState = questionTextEditor.getCurrentContent();
                  if (!contentState.hasText()) return <p className="text-muted">No question text entered</p>;
                  const html = draftContentStateToHtml(contentState);
                  return <div dangerouslySetInnerHTML={{ __html: html }} />;
                } catch (e) {
                  return <p className="text-muted">Unable to render question text</p>;
                }
              })()}
            </div>

            {/* Answer Choices */}
            <div className="mb-4">
              <h6 className="text-uppercase text-muted font-weight-bold mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                <i className="fas fa-list-ul mr-2"></i>Choices
              </h6>
              {choices.map((choice, index) => {
                const isCorrect = correctAnswers.includes(index.toString());
                let choiceHtml = '';
                try {
                  const contentState = choice.text.getCurrentContent();
                  if (contentState.hasText()) {
                    choiceHtml = draftContentStateToHtml(contentState);
                  }
                } catch (e) {}
                return (
                  <div
                    key={index}
                    className="mb-2 p-3 border rounded"
                    style={{ backgroundColor: isCorrect ? '#d4edda' : '#f8f9fa', display: 'flex', alignItems: 'flex-start', width: '100%' }}
                  >
                    <div style={{ marginRight: '12px', flexShrink: 0 }}>
                      <Badge
                        color={isCorrect ? 'success' : 'secondary'}
                        pill
                        style={{
                          minWidth: '32px',
                          height: '32px',
                          fontSize: '0.9rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0'
                        }}
                      >
                        {isCorrect ? '✓' : getChoiceLabel(index)}
                      </Badge>
                    </div>
                    <div style={{ flex: 1, wordBreak: 'break-word', minWidth: 0 }} dangerouslySetInnerHTML={{ __html: choiceHtml || '<em>No text</em>' }} />
                  </div>
                );
              })}
            </div>

            {/* Explanation */}
            <div className="mb-4">
              <h6 className="text-uppercase text-muted font-weight-bold mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                <i className="fas fa-lightbulb mr-2"></i>Explanation
              </h6>
              {(() => {
                try {
                  const contentState = explanationTextEditor.getCurrentContent();
                  if (!contentState.hasText()) return <p className="text-muted">No explanation entered</p>;
                  const html = draftContentStateToHtml(contentState);
                  return <div dangerouslySetInnerHTML={{ __html: html }} />;
                } catch (e) {
                  return <p className="text-muted">Unable to render explanation</p>;
                }
              })()}
            </div>

            {/* Tags */}
            {formData.tags.length > 0 && (
              <div className="mb-3">
                <h6 className="text-uppercase text-muted font-weight-bold mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                  <i className="fas fa-tags mr-2"></i>Tags
                </h6>
                {formData.tags.map((tag, index) => (
                  <Badge key={index} color="info" className="mr-2">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </CardBody>
    </Card>
  );
};

export default QuestionBuilder;
